import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import { DOCUSIGN_RECIPIENT_ID } from "@dune/vars";
import { CheckCircle2, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	useGetEnvelopeTracking,
	useGetSigningUrl,
} from "../../../../../api/mutations/documents.mutations";
import { useGetSocietyFiles } from "../../../../../api/queries/societies.queries";

interface SignConventionDuneModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	societyId: number;
	onSuccess?: () => void;
}

const SignConventionDuneModal: React.FC<SignConventionDuneModalProps> = ({
	open,
	onOpenChange,
	societyId,
	onSuccess,
}) => {
	const [signingUrl, setSigningUrl] = useState<string | null>(null);
	const [signatureCompleted, setSignatureCompleted] = useState(false);
	const [envelopeId, setEnvelopeId] = useState<string | null>(null);

	const getSigningUrlMutation = useGetSigningUrl();

	const {
		data: files,
		isLoading: filesLoading,
		error: filesError,
	} = useGetSocietyFiles(societyId, "CONVENTION");

	useEffect(() => {
		if (files && files.length > 0) {
			const conventionFile = files[0];

			const activeEnvelope = conventionFile.file.docuSignEnvelopes?.[0];
			if (activeEnvelope) {
				setEnvelopeId(activeEnvelope.envelopeId);
			} else {
				const extractedEnvelopeId =
					conventionFile.file.fileData?.metadata?.envelopeId;
				if (extractedEnvelopeId && typeof extractedEnvelopeId === "string") {
					setEnvelopeId(extractedEnvelopeId);
				} else if (open) {
					toast.error(
						"La convention n'a pas encore été envoyée pour signature",
					);
					onOpenChange(false);
				}
			}
		} else if (files && files.length === 0 && open) {
			toast.error("Aucune convention trouvée");
			onOpenChange(false);
		}
	}, [files, open, onOpenChange]);

	const {
		data: tracking,
		isLoading: trackingLoading,
		refetch: refetchTracking,
	} = useGetEnvelopeTracking(envelopeId || "");

	const isDuneSigned =
		tracking?.recipients?.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		)?.status === "SIGNED" ||
		tracking?.recipients?.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		)?.status === "COMPLETED";

	useEffect(() => {
		if (!open) {
			setSigningUrl(null);
			setEnvelopeId(null);
			setSignatureCompleted(false);
		}
	}, [open]);

	useEffect(() => {
		if (filesError && open) {
			console.error("Erreur lors de la récupération des fichiers:", filesError);
			toast.error("Erreur lors de la récupération de la convention");
			onOpenChange(false);
		}
	}, [filesError, open, onOpenChange]);

	const handleStartSigning = async () => {
		if (!envelopeId || !tracking) return;

		const duneRecipient = tracking.recipients.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		);

		if (!duneRecipient) {
			toast.error("Aucun signataire Dune trouvé");
			return;
		}

		try {
			const response = await getSigningUrlMutation.mutateAsync({
				envelopeId: envelopeId,
				recipientId: duneRecipient.recipientId,
			});

			if (response.signingUrl) {
				setSigningUrl(response.signingUrl);
			} else {
				throw new Error("URL de signature non reçue");
			}
		} catch (error) {
			console.error("Erreur lors de la génération de l'URL:", error);

			const errorResponse = error as {
				response?: { data?: { message?: string } };
			};
			const errorMessage = errorResponse?.response?.data?.message;

			if (errorMessage?.includes("déjà été signé")) {
				toast.error("Cette convention a déjà été signée par DUNE");
				refetchTracking();
			} else if (
				errorMessage?.includes("déjà été signée par toutes les parties")
			) {
				toast.error("La convention a déjà été signée par toutes les parties");
				refetchTracking();
			} else if (errorMessage) {
				toast.error(errorMessage);
			} else {
				toast.error(
					"Erreur lors de la génération de l'URL de signature. Veuillez réessayer.",
				);
			}
		}
	};

	const handleSigningComplete = useCallback(() => {
		setSigningUrl(null);
		setSignatureCompleted(true);
		toast.success("Signature terminée avec succès!");

		setTimeout(() => {
			onOpenChange(false);
			onSuccess?.();
		}, 2000);
	}, [onOpenChange, onSuccess]);

	useEffect(() => {
		if (!signingUrl) return;

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;

			if (event.data?.type === "docusign_signing_complete") {
				handleSigningComplete();
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [signingUrl, handleSigningComplete]);

	const isLoading = filesLoading || trackingLoading;
	const canStartSigning = !isLoading && envelopeId && tracking;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={`${
					signingUrl
						? "sm:max-w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col"
						: "sm:max-w-lg"
				}`}
			>
				{signatureCompleted ? (
					<>
						<DialogHeader>
							<DialogTitle>Convention signée avec succès</DialogTitle>
						</DialogHeader>
						<div className="flex flex-col items-center justify-center py-12">
							<CheckCircle2 className="h-16 w-16 text-green-600 mb-6" />
							<h3 className="text-xl font-semibold text-gray-900 mb-2">
								Signature terminée !
							</h3>
							<p className="text-gray-600 text-center max-w-md">
								La convention a été signée avec succès par DUNE. Le document
								signé sera disponible dans quelques instants.
							</p>
						</div>
					</>
				) : !signingUrl ? (
					<>
						<DialogHeader>
							<DialogTitle>Signature de la convention par DUNE</DialogTitle>
							<DialogDescription>
								{isLoading
									? "Chargement des informations de signature..."
									: "Cliquez sur le bouton ci-dessous pour commencer la signature électronique de la convention."}
							</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col items-center justify-center py-6">
							{isLoading ? (
								<Loader2 className="h-8 w-8 animate-spin text-gray-500" />
							) : isDuneSigned ? (
								<>
									<CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
									<p className="text-gray-600 text-center max-w-md">
										Cette convention a déjà été signée par DUNE.
									</p>
								</>
							) : (
								<>
									<p className="text-gray-600 text-center max-w-md mb-6">
										La signature électronique vous permet de signer la
										convention de manière sécurisée directement depuis cette
										interface.
									</p>
									<Button
										onClick={handleStartSigning}
										disabled={
											getSigningUrlMutation.isPending ||
											!canStartSigning ||
											isDuneSigned
										}
										size="lg"
									>
										{getSigningUrlMutation.isPending ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Chargement...
											</>
										) : (
											"Commencer la signature"
										)}
									</Button>
								</>
							)}
						</div>
					</>
				) : (
					<>
						<DialogHeader className="p-4 pb-2 border-b">
							<DialogTitle className="text-lg">
								Signature de la convention par DUNE
							</DialogTitle>
							<DialogDescription className="text-sm">
								Veuillez signer le document dans l'interface ci-dessous. Une
								fois terminé, fermez cette fenêtre.
							</DialogDescription>
						</DialogHeader>
						<div className="flex-1 relative overflow-hidden">
							<iframe
								src={signingUrl}
								className="absolute inset-0 w-full h-full border-0"
								title="DocuSign Signature"
								allow="microphone; camera"
							/>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default SignConventionDuneModal;
